import type { AnswerDetail } from "@/types/forms/answer";
import type { AnsweredFormDetail } from "@/types/forms/answered-form";
import { QuestionType } from "@/types/forms/question";

// A text answer from a link is a value someone typed into an ad URL, not an essay.
const MAX_TEXT_LENGTH = 500;

const TEXT_TYPES = new Set<QuestionType>([
  QuestionType.TEXT,
  QuestionType.TEXT_AREA,
  QuestionType.EMAIL,
  QuestionType.PHONE_NUMBER,
]);
const SINGLE_CHOICE_TYPES = new Set<QuestionType>([
  QuestionType.SINGLE_SELECT,
  QuestionType.RADIAL_SINGLE_SELECT,
  QuestionType.LOCATION,
]);
const MULTI_CHOICE_TYPES = new Set<QuestionType>([
  QuestionType.MULTI_SELECT,
  QuestionType.RADIAL_MULTI_SELECT,
]);

/** Question types a lead form's link can answer. */
export const PREFILLABLE_QUESTION_TYPES = new Set<QuestionType>([
  ...TEXT_TYPES,
  ...SINGLE_CHOICE_TYPES,
  ...MULTI_CHOICE_TYPES,
  QuestionType.NUMBER,
  QuestionType.TOGGLE,
  QuestionType.CHECKBOX,
]);

function options(answer: AnswerDetail): string[] {
  const data = answer.question.data as { options?: unknown };
  return Array.isArray(data.options) ? data.options : [];
}

/**
 * The answer `?<question id>=<value>` gives this question, or undefined when the
 * value isn't one the visitor could have entered. A choice must be sent exactly
 * as picking it would send it: the option itself, or the location's id.
 */
function prefillValue(
  answer: AnswerDetail,
  values: string[],
): AnswerDetail["data"] | undefined {
  const { questionType } = answer.question;
  const [value] = values;
  if (MULTI_CHOICE_TYPES.has(questionType)) {
    const allowed = options(answer);
    const picked = Array.from(new Set(values));
    return picked.length > 0 && picked.every((item) => allowed.includes(item))
      ? picked
      : undefined;
  }
  if (!value?.trim()) return undefined;
  if (TEXT_TYPES.has(questionType)) {
    return value.length <= MAX_TEXT_LENGTH ? value : undefined;
  }
  if (SINGLE_CHOICE_TYPES.has(questionType)) {
    return options(answer).includes(value) ? value : undefined;
  }
  if (questionType === QuestionType.NUMBER) {
    return Number.isFinite(Number(value)) ? value : undefined;
  }
  if (questionType === QuestionType.TOGGLE) {
    if (value === "true") return true;
    if (value === "false") return false;
    return undefined;
  }
  if (questionType === QuestionType.CHECKBOX) {
    // Unticked is unanswered, never false.
    return value === "true" ? true : undefined;
  }
  return undefined;
}

/** Each question of the form the link answers, by question id. */
export function leadFormPrefill(
  form: AnsweredFormDetail,
  search: string,
): Map<string, AnswerDetail["data"]> {
  const params = new URLSearchParams(search);
  const prefill = new Map<string, AnswerDetail["data"]>();
  for (const block of form.blocks) {
    for (const answer of block.answers) {
      const values = params.getAll(answer.question.id);
      if (values.length === 0) continue;
      const value = prefillValue(answer, values);
      if (value !== undefined) prefill.set(answer.question.id, value);
    }
  }
  return prefill;
}
